import React, { useEffect, useRef, useState } from "react"
import { Backdrop, CircularProgress, Container, Snackbar } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import backdropStyles from '../AgentSkillsManagement/styles.module.css'
import styles from './styles.module.css'
import { Pages } from "../../models/Pages"
import {
    BlockedNumber,
    IBlockFilters,
} from "../../models/RobocallBlocks/BlockedNumber"
import { RobocallBlocksActions } from "../../actions/RobocallBlocksActions"
import ControlPanel from "./ControlPanel"
import BlocksList from "./BlocksList"
import CommentDialog, { DialogTone } from "./CommentDialog"
import AddBlock from "./AddBlock"
import ExpiryDialog from "./ExpiryDialog"
import { GreenBtn, RedBtn } from "../Styled/StyledButtons"

/**
 * Robocall review queue — the only place a number becomes a CONFIRMED robocall.
 *
 * Attribution comes from the authenticated Apollo user server-side, which is the whole
 * reason confirmation belongs here rather than in the analytics dashboard: that service is
 * detection-and-evidence only and records no human decisions.
 *
 * Default view is potential numbers, highest score first — the API already orders
 * candidate before active and score descending.
 */
const RobocallBlocks = () => {
    const [filters, setFilters] = useState<IBlockFilters>({ status: 'candidate' })
    const [blocks, setBlocks] = useState<BlockedNumber[]>([])
    const [pagination, setPagination] = useState<Pages | null>(null)
    const [currPage, setCurrPage] = useState(1)
    const [loading, setLoading] = useState('loaded')
    const [backdrop, setBackdrop] = useState(true)
    const [selected, setSelected] = useState<number[]>([])
    const [minSamples, setMinSamples] = useState(3)
    const [vetoConflictCount, setVetoConflictCount] = useState<number | null>(null)

    const [alert, setAlert] = useState<{ severity: 'error' | 'success', text: string } | null>(null)

    const [showAdd, setShowAdd] = useState(false)
    const [expiryTarget, setExpiryTarget] = useState<BlockedNumber | null>(null)
    const [dialog, setDialog] = useState<{
        title: string
        description?: React.ReactNode
        commentRequired: boolean
        actionLabel: string
        tone: DialogTone
        submit: (comment: string) => void
    } | null>(null)

    const searchTimer = useRef<number | null>(null)
    // Filters live in state, but the fetch has to see the value that triggered it — a
    // debounced callback would otherwise close over a stale object.
    const filtersRef = useRef(filters)
    const pageRef = useRef(currPage)
    filtersRef.current = filters
    pageRef.current = currPage

    useEffect(() => {
        loadConfig()
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadConfig = () => {
        // min_duration_samples decides whether a median is trustworthy, and the queue has
        // to warn about it. Read it from the server rather than hardcoding, so retuning
        // the blocker does not silently make this screen lie.
        RobocallBlocksActions.getStats(1)
            .then((data: any) => {
                if (data && data.config && data.config.min_duration_samples) {
                    setMinSamples(data.config.min_duration_samples)
                }
            })
            .catch(() => { /* the queue is still usable without it */ })
    }

    const load = () => {
        setLoading('loading')
        RobocallBlocksActions.getBlocks(filtersRef.current, pageRef.current)
            .then((data: any) => {
                setBlocks(data.data || [])
                setPagination(new Pages(data.per_page, data.current_page, data.last_page))
                setLoading('loaded')
                setBackdrop(false)
                // A bulk action must never reach a row that scrolled out of the result set.
                setSelected([])
            })
            .catch((error) => {
                setAlert({ severity: 'error', text: describe(error) })
                setLoading('loaded')
                setBackdrop(false)
            })
    }

    /** Count of unresolved veto conflicts, for the filter label. */
    useEffect(() => {
        RobocallBlocksActions.getBlocks({ veto_conflicts: true }, 1, 1)
            .then((data: any) => setVetoConflictCount(data.total !== undefined ? data.total : null))
            .catch(() => setVetoConflictCount(null))
    }, [blocks.length])

    const describe = (error: any): string => {
        const data = error?.response?.data
        if (data?.error) {
            return data.error
        }
        if (data?.messages) {
            return Object.keys(data.messages).map((k) => data.messages[k].join(' ')).join(' ')
        }
        return error?.message || 'Request failed'
    }

    const handleFilters = (next: IBlockFilters) => {
        const searchChanged = next.q !== filters.q
        setFilters(next)
        filtersRef.current = next
        setCurrPage(1)
        pageRef.current = 1

        if (searchTimer.current) {
            window.clearTimeout(searchTimer.current)
        }
        // Typing debounces; picking from a dropdown should feel immediate.
        searchTimer.current = window.setTimeout(load, searchChanged ? 800 : 0)
    }

    const handleChangePage = (page: number) => {
        setCurrPage(page)
        pageRef.current = page
        load()
    }

    const after = (message: string) => {
        setAlert({ severity: 'success', text: message })
        load()
    }

    const fail = (error: any) => setAlert({ severity: 'error', text: describe(error) })

    // ---------------------------------------------------------------- single actions

    const confirmOne = (block: BlockedNumber) => setDialog({
        title: `Confirm ${block.phone_e164} is a robocall?`,
        description: <>
            It will start being rejected immediately and stop reaching agents.
            {block.veto_strength > 0 ?
                <><br /><b>This number has real-lead evidence against it</b> (veto ×{block.veto_strength}).
                    Automation refuses to block it; you are overriding that.</>
                : null}
        </>,
        commentRequired: false,
        actionLabel: 'Confirm robocall',
        tone: 'confirm',
        submit: (comment) => RobocallBlocksActions.promote(block.id!, comment || 'Confirmed in the review queue.')
            .then(() => after(`${block.phone_e164} is now blocking.`))
            .catch(fail),
    })

    const clearOne = (block: BlockedNumber) => setDialog({
        title: block.status === 'active'
            ? `Unblock ${block.phone_e164}?`
            : `Mark ${block.phone_e164} as NOT a robocall?`,
        description: <>
            Takes effect on the next call. The number is also veto-protected, so the
            scheduled scan cannot block it again.
        </>,
        commentRequired: true,
        actionLabel: block.status === 'active' ? 'Unblock' : 'Not a robocall',
        tone: 'clear',
        submit: (comment) => RobocallBlocksActions.release(block.id!, comment)
            .then(() => after(`${block.phone_e164} cleared.`))
            .catch(fail),
    })

    // ---------------------------------------------------------------- bulk actions

    const bulk = (action: 'confirm' | 'clear') => setDialog({
        title: action === 'confirm'
            ? `Confirm ${selected.length} number(s) as robocalls?`
            : `Clear ${selected.length} number(s)?`,
        description: action === 'confirm'
            ? <>All of them start being rejected. Applied in one transaction — either every
                row is applied or none is.</>
            : <>All of them stop being rejected and become veto-protected. Applied in one
                transaction.</>,
        commentRequired: action === 'clear',
        actionLabel: action === 'confirm' ? 'Confirm all' : 'Clear all',
        tone: action === 'confirm' ? 'confirm' : 'clear',
        submit: (comment) => RobocallBlocksActions.bulk(action, selected, comment)
            .then((res: any) => {
                const skipped = (res.skipped || []).length
                after(`${res.applied} applied${skipped > 0 ? `, ${skipped} skipped` : ''}.`)
            })
            .catch(fail),
    })

    const toggleSelect = (id: number) => setSelected(
        selected.indexOf(id) === -1 ? [...selected, id] : selected.filter((s) => s !== id)
    )

    const toggleSelectAll = () => {
        const selectable = blocks
            .filter((b) => b.status === 'candidate' || b.status === 'active')
            .map((b) => b.id!)
        const allSelected = selectable.length > 0
            && selectable.every((id) => selected.indexOf(id) !== -1)
        setSelected(allSelected ? [] : selectable)
    }

    return (
        <>
            <ControlPanel
                filters={filters}
                searchStatus={loading}
                vetoConflictCount={vetoConflictCount}
                onChange={handleFilters}
                onAdd={() => setShowAdd(true)}
                onExport={() => RobocallBlocksActions.exportCsv(filters).catch(fail)}
            />

            {selected.length > 0 ?
                <Container maxWidth={false}>
                    <div className={styles.BulkBar}>
                        <span className={styles.BulkCount}>{selected.length} selected</span>
                        <GreenBtn onClick={() => bulk('confirm')}>Confirm as robocalls</GreenBtn>
                        <RedBtn onClick={() => bulk('clear')}>Not robocalls</RedBtn>
                    </div>
                </Container>
                : null}

            <BlocksList
                blocks={blocks}
                pagination={pagination}
                selected={selected}
                minSamples={minSamples}
                handleChangePage={handleChangePage}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onConfirm={confirmOne}
                onClear={clearOne}
                onExpiry={(block) => setExpiryTarget(block)}
            />

            {dialog !== null ?
                <CommentDialog
                    show={true}
                    title={dialog.title}
                    description={dialog.description}
                    commentRequired={dialog.commentRequired}
                    actionLabel={dialog.actionLabel}
                    tone={dialog.tone}
                    toggleHandle={() => setDialog(null)}
                    onSubmit={dialog.submit}
                />
                : null}

            <AddBlock
                show={showAdd}
                toggleHandle={() => setShowAdd(false)}
                onSubmit={(phone, comment, days, permanent) =>
                    RobocallBlocksActions.createBlock(phone, comment, days, permanent)
                        .then(() => after(`${phone} blocked.`))
                        .catch(fail)}
            />

            <ExpiryDialog
                show={expiryTarget !== null}
                target={expiryTarget}
                toggleHandle={() => setExpiryTarget(null)}
                onSubmit={(comment, days, permanent) =>
                    RobocallBlocksActions.changeExpiry(expiryTarget!.id!, comment, days, permanent)
                        .then(() => after('Expiry updated.'))
                        .catch(fail)}
            />

            <Snackbar
                open={alert !== null}
                autoHideDuration={5000}
                onClose={() => setAlert(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={() => setAlert(null)} severity={alert?.severity || 'error'} variant="filled">
                    <div className={backdropStyles.AlertError}>{alert?.text}</div>
                </Alert>
            </Snackbar>

            <Backdrop className={backdropStyles.Backdrop} open={backdrop}>
                <CircularProgress className={backdropStyles.BackdropProgress} />&nbsp;&nbsp;
                <p className={backdropStyles.BackdropProgress}>Loading...</p>
            </Backdrop>
        </>
    )
}

export default RobocallBlocks
